import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let refreshSubscribers = [];

// Function to add failed requests to queue
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to retry all queued requests with new token
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 Unauthorized responses with automatic token refresh
api.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 Unauthorized and we haven't already tried to refresh
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Check if we have a refresh token
      const refreshToken = sessionStorage.getItem('refresh_token');

      if (!refreshToken) {
        // No refresh token, logout
        console.log('No refresh token available. Logging out...');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Token refresh is already in progress, queue this request
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest._retry = true;
            resolve(axios(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: new_refresh_token } = response.data;

        // Store new tokens
        sessionStorage.setItem('access_token', access_token);
        sessionStorage.setItem('refresh_token', new_refresh_token);

        // Update the authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Notify all queued requests
        onTokenRefreshed(access_token);

        isRefreshing = false;

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        console.log('Token refresh failed. Logging out...');
        isRefreshing = false;
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Return the error for other status codes
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => {
    // Clear tokens from sessionStorage
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user');
    return api.post('/auth/logout');
  },
};

// Batch API
export const batchAPI = {
  // Existing methods
  create: (data) => api.post('/batches/create', data),
  getBatch: (batchId) => api.get(`/batches/${batchId}`),
  getImages: (batchId) => api.get(`/batches/${batchId}/images`),
  list: (skip = 0, limit = 100, filterTab = 'all') => api.get('/batches/', { params: { skip, limit, filter_tab: filterTab } }),
  getReport: (fromDate, toDate) => api.get('/batches/report', { params: { from_date: fromDate, to_date: toDate } }),
  delete: (batchId) => api.delete(`/batches/${batchId}`),

  // Edit GroupMetadata file
  editMetadata: (batchId, formData) => {
    return api.put(`/batches/${batchId}/edit-metadata`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Upload folder (new batch creation with folder structure)
  uploadFolder: (formData, onUploadProgress, cancelToken) => {
    return api.post('/batches/upload-folder', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
      cancelToken,
    });
  },

  // Upload methods
  uploadImages: (batchId, formData, onUploadProgress) => {
    return api.post(`/batches/${batchId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  },

  // Alias for backward compatibility
  upload: (batchId, formData, onUploadProgress) => {
    return api.post(`/batches/${batchId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  },

  // Validation methods
  validate: (batchId) => api.post(`/batches/${batchId}/validate`),
  getValidationProgress: (batchId) => api.get(`/batches/${batchId}/progress`),

  // Rework methods
  uploadReworkImages: (batchId, formData, onUploadProgress) => {
    return api.post(`/batches/${batchId}/rework/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  },

  validateReworkImages: (batchId) => {
    return api.post(`/batches/${batchId}/rework/validate`);
  },

  replaceFailed: (batchId) => {
    return api.post(`/batches/${batchId}/replace-failed`);
  },

  // Download methods
  downloadImages: async (batchId, imageIds) => {
    const response = await api.post(
      `/batches/${batchId}/download`,
      imageIds,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `batch_${batchId}_images.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response;
  },

  downloadSingleImage: async (batchId, imageId, filename) => {
    const token = sessionStorage.getItem('access_token');
    const response = await fetch(
      `/api/batches/${batchId}/download/${imageId}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );

    if (!response.ok) throw new Error(`Download failed: ${response.status}`);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Folder structure methods
  getFolderStructure: (batchId) => api.get(`/batches/${batchId}/folder-structure`),

  getFile: (batchId, filePath) => api.get(`/batches/${batchId}/file/${filePath}`),

  // Update GroupMetadata file from edited data
  updateGroupMetadata: (batchId, fileName, data) => {
    return api.put(`/batches/${batchId}/update-group-metadata`, {
      file_name: fileName,
      data: data
    });
  },

  // Metadata validation methods (Gemini AI)
  validateMetadata: (batchId) => api.post(`/batches/${batchId}/validate-metadata`),

  getMetadataValidationStatus: (batchId) => api.get(`/batches/${batchId}/metadata-validation-status`),

  // S3 Direct Upload methods
  requestS3Upload: (data) => api.post('/batches/request-s3-upload', data),

  confirmS3Upload: (batchId) => api.post(`/batches/confirm-s3-upload/${batchId}`),

  // Upload file directly to S3 using presigned URL
  uploadToS3: async (presignedUrl, file, onUploadProgress) => {
    return axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      onUploadProgress,
    });
  },

  // Download combined validation report CSV for selected batch IDs
  downloadValidationReport: async (batchIds) => {
    const response = await api.post('/batches/validation-report', { batch_ids: batchIds }, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const now = new Date();
    const ts = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    link.setAttribute('download', `validation_report_${ts}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return response;
  },

  // SFTP info for vendor
  getSftpInfo: () => api.get('/batches/sftp-info'),

  // S3 Rework Upload methods
  requestReworkS3Upload: (batchId, filePaths) => {
    return api.post(`/batches/${batchId}/rework/request-s3-upload`, {
      file_paths: filePaths
    });
  },

  confirmReworkS3Upload: (batchId) => {
    return api.post(`/batches/${batchId}/rework/confirm-s3-upload`);
  },
};

// User API
export const userAPI = {
  list: (skip = 0, limit = 100) => api.get('/users', { params: { skip, limit } }),
  get: (userId) => api.get(`/users/${userId}`),
  create: (userData) => api.post('/users', userData),
  update: (userId, userData) => api.put(`/users/${userId}`, userData),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Job API
export const jobAPI = {
  list: (skip = 0, limit = 100) => api.get('/jobs', { params: { skip, limit } }),
  get: (jobId) => api.get(`/jobs/${jobId}`),
  create: (jobData) => api.post('/jobs', jobData),
  update: (jobId, jobData) => api.put(`/jobs/${jobId}`, jobData),
};

// Auditor API
export const auditorAPI = {
  // Get all users with batch statistics
  getUserStats: () => api.get('/auditor/users'),

  // Get batches for review (optionally filtered by user and status)
  getBatches: (userId = null, filterStatus = null) => {
    const params = {};
    if (userId) params.user_id = userId;
    if (filterStatus) params.filter_status = filterStatus;
    return api.get('/auditor/batches', { params });
  },

  // Get images for a batch (for per-image reason entry)
  getBatchImages: (batchId) => api.get(`/auditor/batches/${batchId}/images`),

  // Review a batch (approve or reject) with per-image reasons
  reviewBatch: (batchId, reviewData) => {
    return api.patch(`/auditor/batches/${batchId}/review`, reviewData);
  },
};

// Admin API (Tenant and User Management)
export const adminAPI = {
  // Tenant Management
  tenants: {
    list: (skip = 0, limit = 100) => api.get('/admin/tenants', { params: { skip, limit } }),
    get: (tenantId) => api.get(`/admin/tenants/${tenantId}`),
    create: (tenantData) => api.post('/admin/tenants', tenantData),
    update: (tenantId, tenantData) => api.put(`/admin/tenants/${tenantId}`, tenantData),
    toggleStatus: (tenantId) => api.patch(`/admin/tenants/${tenantId}/toggle-status`),
  },

  // User Management
  users: {
    list: (skip = 0, limit = 100, tenantId = null, role = null) => {
      const params = { skip, limit };
      if (tenantId) params.tenant_id = tenantId;
      if (role) params.role = role;
      return api.get('/admin/users', { params });
    },
    get: (userId) => api.get(`/admin/users/${userId}`),
    create: (userData) => api.post('/admin/users', userData),
    update: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
    delete: (userId) => api.delete(`/admin/users/${userId}`),
    resetPassword: (userId, passwordData) => api.post(`/admin/users/${userId}/reset-password`, passwordData),
    enableSftp: (userId) => api.post(`/admin/users/${userId}/sftp-access`),
    resetSftpPassword: (userId) => api.post(`/admin/users/${userId}/sftp-reset`),
    getSftpStatus: (userId) => api.get(`/admin/users/${userId}/sftp-status`),
    getActiveJob: (userId) => api.get(`/admin/users/${userId}/job`),
  },

  // Job Management
  jobs: {
    list: () => api.get('/admin/jobs'),
    create: (jobData) => api.post('/admin/jobs', jobData),
    getUsers: (jobId) => api.get(`/admin/jobs/${jobId}/users`),
    assignUser: (jobId, userId) => api.post(`/admin/jobs/${jobId}/assign/${userId}`),
    unassignUser: (jobId, userId) => api.delete(`/admin/jobs/${jobId}/unassign/${userId}`),
  },
};

export default api;